import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Send, Loader2, MessageCircle, Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/gtag";
import { useTranslation } from "react-i18next";
import { CONTACT, SERVICE_INTERESTS } from "@/lib/constants";
import { useIdentity } from "@/context/SiteConfigContext";
import { countryImage } from "@/lib/images";

const infoCards = [
  { labelKey: "contact.infoCall", tid: "call", icon: Phone, value: CONTACT.phone, href: CONTACT.phoneHref, tint: "text-orange-600 bg-orange-50 border-orange-100" },
  { labelKey: "contact.infoEmail", tid: "email", icon: Mail, value: CONTACT.email, href: `mailto:${CONTACT.email}`, tint: "text-primary bg-secondary border-border" },
  { labelKey: "contact.infoOffice", tid: "head", icon: MapPin, value: CONTACT.address, href: null, tint: "text-orange-600 bg-orange-50 border-orange-100" },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "", country_residence: "", preferred_jurisdiction: "", hp: "" });
  const [consent, setConsent] = useState(false);
  const { t } = useTranslation();
  const [jurisdictions, setJurisdictions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    api.get("/catalog").then((r) => setJurisdictions((r.data.countries || []).map((c) => c.name))).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@") || !form.message.trim()) {
      return toast.error(t("forms.required"));
    }
    if (!consent) return toast.error(t("forms.consent"));
    setBusy(true);
    try {
      const u = new URLSearchParams(window.location.search);
      await api.post("/contact", {
        ...form, type: "contact", consent,
        source_url: window.location.href,
        utm_source: u.get("utm_source") || "", utm_medium: u.get("utm_medium") || "", utm_campaign: u.get("utm_campaign") || "",
      });
      setSent(true);
      // Fire lead event ONLY on successful submission (not on page load).
      trackEvent("generate_lead", { form: "contact", service: form.service || "unspecified" });
      toast.success(t("forms.successBody"));
      setForm({ name: "", email: "", phone: "", service: "", message: "", country_residence: "", preferred_jurisdiction: "", hp: "" });
      setConsent(false);
    } catch (err) {
      const msg = err?.response?.status === 429 ? t("contact.errorRateLimit") : t("contact.errorGeneric");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative grain pt-36 pb-16 border-b border-border bg-secondary/40">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-xs uppercase tracking-[0.2em] text-orange-600">{t("contact.kicker")}</p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-primary">{t("contact.title")}</h1>
            <p className="mt-5 text-muted-foreground text-lg leading-relaxed max-w-2xl">
              {t("contact.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20 grid lg:grid-cols-5 gap-10">
        {/* Form */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="lg:col-span-3 rounded-2xl border border-border bg-card p-6 sm:p-9 card-soft" data-testid="contact-form-card">
          <h2 className="font-heading text-2xl tracking-tight text-primary">{t("contact.formTitle")}</h2>
          <p className="mt-1.5 text-sm text-muted-foreground">{t("contact.formRequiredNote")}</p>
          <form onSubmit={submit} className="mt-7 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("contact.labelName")}</label>
                <Input value={form.name} onChange={set("name")} placeholder={t("forms.namePlaceholder")} className="mt-2 bg-background border-border" data-testid="contact-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("contact.labelEmail")}</label>
                <Input type="email" value={form.email} onChange={set("email")} placeholder={t("forms.emailPlaceholder")} className="mt-2 bg-background border-border" data-testid="contact-email" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("contact.labelPhone")}</label>
                <Input value={form.phone} onChange={set("phone")} placeholder={t("contact.phonePlaceholder")} className="mt-2 bg-background border-border" data-testid="contact-phone" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("contact.labelInterest")}</label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger className="mt-2 bg-background border-border" data-testid="contact-service"><SelectValue placeholder={t("forms.selectService")} /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_INTERESTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("contact.labelResidence")}</label>
                <Input value={form.country_residence} onChange={set("country_residence")} placeholder={t("contact.residencePlaceholder")} className="mt-2 bg-background border-border" data-testid="contact-country" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("contact.labelJurisdiction")}</label>
                <Select value={form.preferred_jurisdiction} onValueChange={set("preferred_jurisdiction")}>
                  <SelectTrigger className="mt-2 bg-background border-border" data-testid="contact-jurisdiction"><SelectValue placeholder={t("contact.notSure")} /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="Not sure">{t("contact.notSure")}</SelectItem>
                    {jurisdictions.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("contact.labelHelp")}</label>
              <Textarea value={form.message} onChange={set("message")} placeholder={t("forms.messagePlaceholder")} className="mt-2 min-h-[130px] bg-background border-border" data-testid="contact-message" />
            </div>
            {/* Honeypot — hidden from users, catches bots */}
            <input type="text" tabIndex={-1} autoComplete="off" value={form.hp} onChange={set("hp")} className="hidden" aria-hidden="true" data-testid="contact-hp" />
            <label className="flex items-start gap-3 cursor-pointer text-sm text-muted-foreground">
              <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="contact-consent" />
              <span>{t("contact.consentPre")} <Link to="/legal/privacy" className="text-orange-700 hover:underline">{t("footer.privacy")}</Link> {t("contact.consentPost")}</span>
            </label>
            <Button type="submit" disabled={busy || !consent} size="lg" className="rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-12 px-8 w-full sm:w-auto disabled:opacity-50" data-testid="contact-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : sent ? <>{t("forms.successTitle")} ✓</> : <>{t("forms.send")} <Send className="h-4 w-4 ml-2" /></>}
            </Button>
          </form>
        </motion.div>

        {/* Info column */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="lg:col-span-2 space-y-4" data-testid="contact-info">
          <div className="rounded-2xl overflow-hidden ring-1 ring-border card-soft relative">
            <img src={countryImage("hongkong")} alt="Hong Kong office" className="w-full h-44 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/70 to-transparent" />
            <div className="absolute bottom-4 left-5 text-primary-foreground"><p className="text-xs uppercase tracking-[0.2em] opacity-80">{t("contact.headOffice")}</p><p className="font-heading text-lg">{t("contact.headOfficeCity")}</p></div>
          </div>
          {infoCards.map((c) => {
            const inner = (
              <div className="flex items-start gap-4">
                <span className={`grid place-items-center h-10 w-10 rounded-sm border shrink-0 ${c.tint}`}><c.icon className="h-5 w-5" /></span>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">{t(c.labelKey)}</p><p className="mt-1 text-sm text-primary font-medium leading-snug">{c.value}</p></div>
              </div>
            );
            return c.href ? (
              <a key={c.labelKey} href={c.href} className="block rounded-xl border border-border bg-card p-5 card-soft hover:border-orange-300 transition-colors" data-testid={`contact-info-${c.tid}`}>{inner}</a>
            ) : (
              <div key={c.labelKey} className="rounded-xl border border-border bg-card p-5 card-soft" data-testid={`contact-info-${c.tid}`}>{inner}</div>
            );
          })}
          <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-xl border border-[#25D366]/40 bg-[#25D366]/10 p-5 hover:bg-[#25D366]/15 transition-colors" data-testid="contact-whatsapp">
            <div className="flex items-center gap-4"><span className="grid place-items-center h-10 w-10 rounded-sm bg-[#25D366] text-white"><MessageCircle className="h-5 w-5" /></span><div><p className="text-xs uppercase tracking-wider text-muted-foreground">{t("contact.chatInstantly")}</p><p className="mt-1 text-sm text-primary font-medium">{t("contact.whatsappTeam")}</p></div></div>
            <ArrowRight className="h-4 w-4 text-[#128C7E]" />
          </a>
          <div className="rounded-xl border border-border bg-secondary/40 p-5 flex items-center gap-3 text-sm text-muted-foreground"><Clock className="h-4 w-4 text-orange-600" /> {t("contact.hours")}</div>
        </motion.div>
      </section>
      <LegalIdentity />
      <Footer />
    </div>
  );
}

function LegalIdentity() {
  const id = useIdentity();
  const { t } = useTranslation();
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16" data-testid="contact-legal-identity">
      <div className="rounded-xl border border-border bg-card p-6 card-soft text-sm">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">{t("contact.legalTitle")}</p>
        <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-muted-foreground">
          <div className="flex justify-between gap-4"><dt>{t("contact.legalEntity")}</dt><dd className="text-foreground text-right">{id.legalName}</dd></div>
          <div className="flex justify-between gap-4"><dt>{t("contact.legalRegNumber")}</dt><dd className="text-foreground text-right">{id.regNumber}</dd></div>
          <div className="flex justify-between gap-4"><dt>{t("contact.legalJurisdiction")}</dt><dd className="text-foreground text-right">{id.jurisdiction}</dd></div>
          <div className="flex justify-between gap-4"><dt>{t("contact.legalOffice")}</dt><dd className="text-foreground text-right">{id.office}</dd></div>
          <div className="flex justify-between gap-4"><dt>{t("contact.legalEmail")}</dt><dd className="text-foreground text-right"><a href={`mailto:${id.email}`} className="hover:text-orange-700">{id.email}</a></dd></div>
          <div className="flex justify-between gap-4"><dt>{t("contact.legalWebsite")}</dt><dd className="text-foreground text-right"><a href={id.website} target="_blank" rel="noreferrer" className="hover:text-orange-700">{id.website.replace(/^https?:\/\//, "")}</a></dd></div>
        </dl>
      </div>
    </section>
  );
}
